<template>
  <div class="home-container">
    <!-- Hero Section -->
    <div class="hero-section py-5">
      <div class="container-fluid">
        <div class="row align-items-center">
          <div class="col-lg-8">
            <h1 class="display-4 fw-bold text-gradient">
              <i class="bi bi-satellite-dish me-3"></i>
              MyGeotab Feeder Monitor
            </h1>
            <p class="lead mb-4 text-secondary">
              Sistema integral de gestión de clientes y monitoreo de datos de rastreo vehicular en tiempo real
            </p>
          </div>
          <div class="col-lg-4 text-center">
            <div class="card system-status-card">
              <div class="card-body">
                <h5 class="mb-3 text-primary">Estado del Sistema</h5>
                <div v-if="systemHealth" class="status-indicator">
                  <div class="d-flex align-items-center justify-content-center mb-3">
                    <i
                      class="bi fs-1 me-2"
                      :class="systemHealth.status === 'healthy' ? 'bi-check-circle-fill text-success' : 'bi-exclamation-triangle-fill text-warning'"
                    ></i>
                    <div class="text-start">
                      <div class="fw-bold">
                        {{ systemHealth.status === 'healthy' ? 'Sistema Activo' : 'Sistema con Problemas' }}
                      </div>
                      <small class="text-muted">
                        Última verificación: {{ formatTime(systemHealth.timestamp) }}
                      </small>
                    </div>
                  </div>
                  <div v-if="systemHealth.database" class="small">
                    <div class="d-flex justify-content-between">
                      <span>Base de Datos:</span>
                      <span class="badge" :class="systemHealth.database.status === 'healthy' ? 'badge-success' : 'badge-danger'">
                        {{ systemHealth.database.status === 'healthy' ? 'Conectada' : 'Desconectada' }}
                      </span>
                    </div>
                  </div>
                </div>
                <div v-else class="text-center">
                  <div class="spinner spinner-sm" role="status">
                    <span class="visually-hidden">Verificando...</span>
                  </div>
                  <div class="small mt-2 text-muted">Verificando estado...</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- System Info -->
    <div class="system-info-section py-5">
      <div class="container-fluid">
        <div class="row">
          <div class="col-lg-8 mx-auto">
            <div class="card">
              <div class="card-header">
                <h5 class="card-title mb-0">
                  <i class="bi bi-info-circle me-2 text-primary"></i>
                  Información del Sistema
                </h5>
              </div>
              <div class="card-body">
                <div class="row g-4">
                  <div class="col-md-4">
                    <div class="text-center">
                      <i class="bi bi-gear-fill fs-2 text-primary mb-2"></i>
                      <h6 class="fw-semibold">Versión</h6>
                      <span class="badge badge-primary">v2.1.0</span>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="text-center">
                      <i class="bi bi-calendar-event fs-2 text-success mb-2"></i>
                      <h6 class="fw-semibold">Última Actualización</h6>
                      <span class="badge badge-success">{{ lastUpdate }}</span>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="text-center">
                      <i class="bi bi-shield-check fs-2 text-info mb-2"></i>
                      <h6 class="fw-semibold">Estado</h6>
                      <span class="badge badge-info">Operativo</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { useClientStore } from '../stores/clients'
import { systemApi } from '../services/api'

export default {
  name: 'Home',
  setup() {
    const clientStore = useClientStore()
    const systemHealth = ref(null)
    const systemStats = ref({
      uptime: '99.9%',
      status: 'Estable'
    })
    const clientStats = ref({
      total: 0,
      active: 0
    })
    const recentActivity = ref({
      count: 0
    })
    const alerts = ref({
      count: 0
    })
    const lastUpdate = ref('')

    let healthCheckInterval = null

    // Methods
    const checkSystemHealth = async () => {
      try {
        const health = await systemApi.healthCheck()
        systemHealth.value = health
      } catch (error) {
        console.error('Error checking system health:', error)
        systemHealth.value = {
          status: 'unhealthy',
          timestamp: new Date().toISOString(),
          database: { status: 'unhealthy' }
        }
      }
    }

    const loadClientStats = async () => {
      try {
        await clientStore.fetchClients(1, 100) // Get first 100 to calculate stats
        clientStats.value = {
          total: clientStore.totalClients,
          active: clientStore.activeClients
        }
      } catch (error) {
        console.error('Error loading client stats:', error)
      }
    }

    const loadSystemStats = () => {
      // Mock data for now
      recentActivity.value.count = Math.floor(Math.random() * 50) + 10
      alerts.value.count = Math.floor(Math.random() * 5)
      lastUpdate.value = new Date().toLocaleDateString('es-ES')
    }

    const formatTime = (timestamp) => {
      return new Date(timestamp).toLocaleString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }

    // Lifecycle
    onMounted(async () => {
      // Load initial data
      await Promise.all([
        checkSystemHealth(),
        loadClientStats(),
        loadSystemStats()
      ])

      // Set up periodic health checks
      healthCheckInterval = setInterval(checkSystemHealth, 30000) // Every 30 seconds
    })

    onUnmounted(() => {
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval)
      }
    })

    return {
      systemHealth,
      systemStats,
      clientStats,
      recentActivity,
      alerts,
      lastUpdate,
      formatTime
    }
  }
}
</script>

<style lang="scss" scoped>
/* Modern Dark Theme Styles for Home Component */

.hero-section {
  background: linear-gradient(135deg, var(--bg-primary) 0%, rgba(191, 212, 240, 0.95) 95%, var(--bg-secondary) 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 80' width='80' height='80'%3E%3Cg fill-opacity='0.03'%3E%3Cpath fill='%23ffffff' d='M0 0h80v80H0V0zm20 20v40h40V20H20zm20 35a15 15 0 1 1 0-30 15 15 0 0 1 0 30z'/%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.1;
    z-index: 0;
  }

  .container-fluid {
    position: relative;
    z-index: 1;
  }
}

.system-status-card {
  backdrop-filter: blur(20px);
  border: 1px solid rgba(59, 130, 246, 0.3);

  &:hover {
    border-color: rgba(59, 130, 246, 0.5);
    box-shadow: var(--box-shadow-lg), var(--glow-primary);
  }
}

.status-indicator {
  animation: pulseGlow 3s ease-in-out infinite;
}

@keyframes pulseGlow {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.02);
  }
}

.metric-card {
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent, rgba(59, 130, 246, 0.03), transparent);
    transform: rotate(45deg);
    transition: var(--transition-slow);
    opacity: 0;
  }

  &:hover::after {
    opacity: 1;
    animation: shimmer 1.5s ease-in-out;
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
}

.quick-actions-section {
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }

  .card {
    transition: var(--transition-spring);

    &:hover {
      transform: translateY(-8px) scale(1.02);
      box-shadow: var(--box-shadow-xl);

      .card-body {
        background: linear-gradient(135deg, transparent, rgba(59, 130, 246, 0.05));
      }
    }
  }
}

.system-info-section {
  .card {
    backdrop-filter: blur(10px);
  }

  .badge {
    font-size: 0.85rem;
    padding: 8px 16px;
  }

  i {
    transition: var(--transition);
  }

  .col-md-4:hover i {
    transform: scale(1.1) rotate(5deg);
  }
}

/* Enhanced animations for icons */
.bi-satellite-dish {
  animation: satelliteRotate 20s linear infinite;
}

@keyframes satelliteRotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Responsive enhancements */
@media (max-width: 768px) {
  .hero-section {
    padding: 2rem 0;

    .display-4 {
      font-size: 2rem;
    }

    .lead {
      font-size: 1rem;
    }
  }

  .system-status-card {
    margin-top: 2rem;
  }

  .metric-card .fs-2 {
    font-size: 1.5rem !important;
  }
}

@media (max-width: 576px) {
  .quick-actions-section {
    .card-body {
      padding: 1.5rem;
    }

    .rounded-circle {
      width: 48px !important;
      height: 48px !important;
    }
  }
}
</style>